"use client";

import { Check, X } from "lucide-react";
import type { User } from "@/lib/types/user";
import { cn } from "@/lib/utils";

export type RoleRow = { id: number; name: string; description?: string | null; is_system?: number };

/** Account can sign in — same pill style as Settings branch + Admin roles toggles. */
export function AccountActivePill({
  checked,
  disabled,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Account active"
      className={cn(
        "inline-flex shrink-0 rounded-full border border-border/80 bg-muted/45 p-0.5 shadow-inner dark:bg-muted/25",
        "ring-offset-background",
      )}
    >
      <button
        type="button"
        role="radio"
        aria-checked={!checked}
        disabled={disabled}
        onClick={() => {
          if (checked) {
            onCheckedChange(false);
          }
        }}
        className={cn(
          "flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          !checked
            ? "bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-sm dark:from-slate-600 dark:to-slate-800"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        <X className="size-3 shrink-0 opacity-90" strokeWidth={2.5} />
        Off
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
          if (!checked) {
            onCheckedChange(true);
          }
        }}
        className={cn(
          "flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          checked
            ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        <Check className="size-3 shrink-0 opacity-90" strokeWidth={2.5} />
        On
      </button>
    </div>
  );
}

export function apiErr(e: unknown): string {
  const ax = e as { response?: { data?: { error?: string } } };
  return ax.response?.data?.error ?? (e instanceof Error ? e.message : "Request failed");
}

export function normalizeRoleId(id: string | number | null | undefined): number | null {
  if (id === null || id === undefined || id === "") {
    return null;
  }
  const n = Number(id);

  return Number.isFinite(n) && n > 0 ? n : null;
}

export function buildRowRoles(user: User | null | undefined, roleOptions: RoleRow[], rolesAll: RoleRow[]): RoleRow[] {
  if (!user) {
    return roleOptions.length > 0 ? roleOptions : rolesAll;
  }
  const base = roleOptions.length > 0 ? roleOptions : rolesAll;
  const seen = new Set(base.map((r) => r.id));
  const rid = normalizeRoleId(user.role_id as string | number | null | undefined);
  const current =
    (rid !== null ? rolesAll.find((r) => r.id === rid) : undefined) ??
    rolesAll.find((r) => r.name === user.role);
  if (current && !seen.has(current.id)) {
    return [...base, current].sort((a, b) => a.id - b.id);
  }

  return base;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
